import {BackendModule} from './../modules/backend.module';
import {Injectable} from '@angular/core';
import {UserSession} from '../interfaces/user-session';
import {Profile} from '../interfaces/profile';
import {Storage} from '@ionic/storage';
import {PlatformService} from './platform';
import {VersionService} from './version';

const SESSION_KEY = 'fitpass.app2'

@Injectable()
export class SessionService {

  user: UserSession;

  nextPayment: String;
  nextPaymentBeauty: String;

  requestInvoiceURL: string;

  // @deprecated use requestInvoiceURL instead
  billingHTML: string;

  //used in the home page header
  reservations = {
    reserved: [],
    past: [],
    current: [],
    assisted: [],
    toReview: []
  };

  beautyReservations = {
    reserved: [],
    past: [],
    current: [],
    assisted: [],
    toReview: []
  };

  defaultProfile: Profile = {
    birthday: null,
    birthdayDate: null,
    gender: null,
    address1: null,
    address2: null,
    city: 1,
    state: 1,
    postal_code: null,
    phone_number: null,
    emergency_contact_name: null,
    emergency_phone_number: null
  };

  constructor(
    private storage: Storage,
    public backendModule: BackendModule,
    public platformService: PlatformService,
    public versionService: VersionService,
  ) {
  }

  // What: Is the user's subscription in the process of being cancelled?
  // Why: Coz we allow users to book classes through their final cycle dates.
  //
  // Specifically, return true if:
  // - user has a subscription with status "canceled" and a cancellation date
  // - the user's cycle dates have a "to_date"
  // - the cancellation date is after today
  isSubscriptionToCancel(): boolean {
    if (
      !this.user ||
      !this.user.subscription ||
      !this.user.subscription.cancelation_date
    ) {
      return false;
    }

    if (this.user.subscription.status !== 'canceled') {
      return false;
    }

    if (!this.user.cycle_dates || !this.user.cycle_dates.to_date) {
      return false;
    }

    let cancelationDate = new Date(
      this.user.cycle_dates.to_date + 'T23:59:59.000Z'
    ).getTime();
    return cancelationDate > new Date().getTime();
  }

  isBeautySubscriptionToCancel(): boolean {
    if (
      !this.user ||
      !this.user.beauty_subscription ||
      !this.user.beauty_subscription.cancelation_date
    ) {
      return false;
    }

    if (this.user.beauty_subscription.status !== 'canceled') {
      return false;
    }


    if (!this.user.cycle_dates_beauty || !this.user.cycle_dates_beauty.to_date) {
      return false;
    }

    const cancelationDate = new Date(
      this.user.cycle_dates_beauty.to_date + 'T23:59:59.000Z'
    ).getTime();

    return cancelationDate > new Date().getTime();
  }


  hasActiveSubscription(): boolean {
    if (!this.user || !this.user.subscription) {
      return false;
    }
    return this.user.subscription.status === 'active';
  }

  hasActiveBeautySubscription(): boolean {
    if (!this.user || !this.user.beauty_subscription) {
      return false;
    }

    return this.user.beauty_subscription.status === 'active';
  }

  load = () => {
    return new Promise((resolve, reject) => {
      this.storage
        .get(SESSION_KEY)
        .then(data => {
          if (!data) {
            resolve(false);
          }
          data = JSON.parse(data);
          if (data.user != null) {
            this.user = data.user;
            if (data.user.profile) {
              if (data.user.profile.birthday) {
                data.user.profile.birthdayDate = new Date(
                  data.user.profile.birthday
                );
              }
              if (data.user.profile.postal_code) {
                data.user.profile.postal_code = parseInt(
                  data.user.profile.postal_code,
                  10
                );
              }
            }
            if (this.user.cycle_dates) {
              this.countClasses(this.user.reservations);
            }
          }
          resolve(true);
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  save() {
    let objectToSave = {
      user: this.user
    };
    this.storage
      .set(SESSION_KEY, JSON.stringify(objectToSave))
      .then(resp => {
        this.countClasses(this.user.reservations);
      })
      .catch(err => {
        this.reportError('sessionService', 'save()', err);
      });
  }

  destroy() {
    this.storage
      .remove(SESSION_KEY)
      .then(resp => {
      })
      .catch(err => {
        this.reportError('sessionService', 'destroy()', err);
      });
    this.user = null;
    this.reservations = {
      reserved: [],
      past: [],
      current: [],
      assisted: [],
      toReview: []
    };
    this.beautyReservations = {
      reserved: [],
      past: [],
      current: [],
      assisted: [],
      toReview: []
    };
  }

  async loadBeautyReservations() {
    const result: any = await this.backendModule
      .getAPI(`users/${this.user.id}/lessons?product=beauty`)
      .toPromise();

    this.countBeautyClasses(result.lessons);
  }

  countBeautyClasses(beautyReservations) {
    this.beautyReservations = {
      reserved: [],
      past: [],
      current: [],
      assisted: [],
      toReview: []
    };


    if (this.user.cycle_dates_beauty == null) {
      return;
    }

    const today = new Date();
    const paymentDate = new Date(this.user.cycle_dates_beauty.from_date);

    for (let i = 0; i < beautyReservations.length; i++) {
      if (beautyReservations[i].schedule.reservation.status === 'cancelled') {
        continue
      }

      const reservationTime = new Date(beautyReservations[i].schedule.lesson_time);
      if (reservationTime < today) {
        if (reservationTime < paymentDate) {
          this.beautyReservations.past.push(beautyReservations[i]);
        } else {
          if (beautyReservations[i].schedule.reservation.status === 'attended') {
            this.beautyReservations.assisted.push(beautyReservations[i]);
            if (!beautyReservations[i].schedule.reservation.review) {
              this.beautyReservations.toReview.push(beautyReservations[i]);
            }
          }
          this.beautyReservations.current.push(beautyReservations[i]);
        }
      } else {
        this.beautyReservations.reserved.unshift(beautyReservations[i]);
      }
    }
  }

  countClasses(reservations) {
    this.reservations = {
      reserved: [],
      past: [],
      current: [],
      assisted: [],
      toReview: []
    };

    var today = new Date();
    var paymentDate = new Date(this.user.cycle_dates.from_date);

    for (var i = 0; i < reservations.length; i++) {
      if (reservations[i].schedule.reservation.status === 'cancelled') {
        continue;
      }

      var reservationTime = new Date(reservations[i].schedule.lesson_time);
      if (reservationTime < today) {
        if (reservationTime < paymentDate) {
          this.reservations.past.push(reservations[i]);
        } else {

          if (reservations[i].schedule.reservation.status === 'attended') {
            this.reservations.assisted.push(reservations[i]);
            if (!reservations[i].schedule.reservation.review) {
              this.reservations.toReview.push(reservations[i]);
            }
          }
          this.reservations.current.push(reservations[i]);

        }
      } else {
        this.reservations.reserved.unshift(reservations[i]);
      }
    }
  }

  reportError(component: string, funct: string, payload?, email?: string) {
    this.backendModule
      .postAPI('errors', {
        payload: JSON.stringify({
          error: payload,
          component: component,
          function: funct,
          platform: this.platformService.platform,
          version: this.versionService.appVersion,
        }),
        email:
          this.user && this.user.email ? this.user.email : email ? email : null,
        user_id: this.user && this.user.id ? this.user.id : null
      })
      .subscribe(
        () => {
          console.log(`%c${component} ` + funct + payload, 'color:#fd766e;');
        },
        err => {
          console.log(
            `%c${component} ` + funct + payload + err,
            'color:#fd766e;'
          );
        }
      );
  }
}



// WEBPACK FOOTER //
// ./src/services/session.ts